import React, { createContext, useContext, useState, useEffect } from 'react';
import { resolveBackendUrl } from '../utils/backendUrl';

const AuthContext = createContext();

// Export the context for direct use in components
export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Get backend URL from environment
  const backendUrl = resolveBackendUrl();
  const backendInvalid = !backendUrl || /none\.preview|undefined|null|^https?:\/\/$/i.test(String(backendUrl || ''));

  // API call helper
  const apiCall = async (endpoint, options = {}) => {
    // For Ghost Agent or AI-heavy endpoints, allow caller to pass higher timeoutMs

    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const url = `${backendUrl}${endpoint}`;
    const timeoutMs = Number(options.timeoutMs || 20000); // default 20s, override per call

    try {
      // Create AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.detail || `HTTP ${response.status}`);
        // attach status and raw data for feature-specific handling (e.g., upgrade prompt)
        err.status = response.status;
        err.data = errorData;
        throw err;
      }

      let data = null;
      try {
        // Prefer JSON but guard for empty bodies or non-JSON content
        data = await response.json();
      } catch (e) {
        try {
          const text = await response.text();
          if (!text || text.trim().length === 0) {
            data = {};
          } else {
            try {
              data = JSON.parse(text);
            } catch (parseErr) {
              data = { raw: text };
            }
          }
        } catch {
          data = {};
        }
      }
      // Safe logging without assuming object
      return data;
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('[API] Request was aborted due to timeout');
        throw new Error('Request timed out. Please check your connection and try again.');
      }
      console.error('[API] Request failed:', error.name, error.message);
      throw error;
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      // Auto-login for local development
      const devEmail = process.env.REACT_APP_DEV_EMAIL;
      const devPassword = process.env.REACT_APP_DEV_PASSWORD;
      if (process.env.NODE_ENV === 'development' && devEmail && devPassword && !localStorage.getItem('token')) {
        try {
          const response = await fetch(`${backendUrl}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: devEmail, password: devPassword }),
          });
          if (response.ok) {
            const data = await response.json();
            if (data.access_token) localStorage.setItem('token', data.access_token);
          }
        } catch {}
      }
      
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          // If IG is connected, pull latest handle/followers into the profile before rendering
          let igProfile = null;
          try { igProfile = await apiCall('/api/ig/profile'); } catch {}
          const profileData = await apiCall('/api/user/profile');
          // If IG has a profile picture, prefer it
          try {
            const igPic = igProfile?.profile_picture_url || igProfile?.picture_url || igProfile?.profile_picture;
            if (igPic && typeof igPic === 'string') {
              profileData.profile_picture_url = igPic;
              profileData.profile_picture = igPic;
            }
          } catch {}
          setUser({ id: profileData.id });
          setProfile(profileData);
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('token');
          setUser(null);
          setProfile(null);
        }
      }
      
      setLoading(false);
    };

    initAuth();
  }, []);

  // Sign up
  const signUp = async (email, password, userData) => {
    setIsAuthenticating(true);
    try {
      const response = await apiCall('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          first_name: userData.first_name,
          last_name: userData.last_name,
          sport: userData.sport,
          school: userData.school,
        }),
      });

      if (response.access_token) {
        localStorage.setItem('token', response.access_token);
        setUser({ id: response.user_id });
        const profileData = await apiCall('/api/user/profile');
        setProfile(profileData);
        return { success: true };
      } else {
        return { error: { message: 'Registration failed. Please try again.' } };
      }
    } catch (error) {
      console.error('Sign up error:', error);
      // Provide user-friendly error message
      const errorMessage = error.message.includes('already exists') || error.message.includes('duplicate')
        ? 'An account with this email already exists. Please try logging in instead.'
        : error.message || 'Registration failed. Please try again.';
      
      return { error: { message: errorMessage } };
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Sign in
  const signIn = async (email, password) => {
    setIsAuthenticating(true);
    try {
      const response = await apiCall('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (response.access_token) {
        localStorage.setItem('token', response.access_token);
        setUser({ id: response.user_id });
        const profileData = await apiCall('/api/user/profile');
        setProfile(profileData);
        
        return { success: true };
      } else {
        return { error: { message: 'Invalid email or password. Please try again.' } };
      }
    } catch (error) {
      console.error('Sign in error:', error);
      // Provide user-friendly error message
      const errorMessage = error.message.includes('Invalid credentials') || error.message.includes('401')
        ? 'Incorrect email or password. Please try again.'
        : error.message || 'Authentication failed. Please try again.';
      
      return { error: { message: errorMessage } };
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Sign out
  const signOut = async () => {
    // Clear cached avatar when signing out
    try {
      localStorage.removeItem('nv_avatar_url');
      localStorage.removeItem('nv_profile_cache');
    } catch {}
    
    localStorage.removeItem('token');
    setUser(null);
    setProfile(null);
    
    // Redirect to landing page with full refresh to reset scroll position
    window.location.href = '/';
  };

  // Refresh profile from server
  const refreshProfile = async () => {
    try {
      const updatedProfile = await apiCall('/api/user/profile');
      setProfile(updatedProfile);
      return updatedProfile;
    } catch (error) {
      console.error('Profile refresh error:', error);
      throw error;
    }
  };

  // Update profile
  const updateProfile = async (profileData) => {
    try {
      await apiCall('/api/user/profile', {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });
      
      // Refresh profile
      const updatedProfile = await refreshProfile();
      
      return { success: true, profile: updatedProfile };
    } catch (error) {
      console.error('Profile update error:', error);
      return { error: { message: error.message } };
    }
  };

  const value = {
    user,
    profile,
    loading,
    isAuthenticating,
    signUp,
    signIn,
    signOut,
    updateProfile,
    refreshProfile,
    apiCall,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};